import { Component, ElementRef, OnInit, Input, Output, ContentChild, EventEmitter } from '@angular/core';
import { ColorSchemes } from './color-schemes';
import { CfCoreComponent } from '../core/core.component';
import { ChartsColorScheme, ChartModel } from '../../models/chart/chart.model';
import { ChartStylingModel } from '../../models/chart/chart-styling.model';
import { TemplateService } from '../../services/template-service/template.service';
import { CfEventService } from '../../services/event-service/event.service';
import { EventDataModel } from '../../models/events/event-data.model';
/**
 * <p>CF Chart component is created based on <a href="https://github.com/swimlane/ngx-charts" target="_blank">Ngx-charts</a></p>
 */
export class CfChartComponent extends CfCoreComponent implements OnInit {
/**
 * @hidden
 */
cfComponentChartTemplate;
/**
 * @hidden
 */
cfChartHost;
/**
 * @hidden
 */
cfNgxChart;
/**
 * <p>Chart component model object. Refer to ../models/chart/chart.model.ts.</p>
 */
properties: any;
/**
 * <p>The styling object of the chart component. Refer to ../models/chart/chart-styling.model.ts</p>
 */
styling: ChartStylingModel;
/**
 * <p>Data to show in the chart format</p>
 * @param {?} value
 * @return {?}
 */
set data(value: any[]) { this.properties.data = value; }
/**
 * Dimensions of chart [width, height] by px
 * @param {?} value
 * @return {?}
 */
set view(value: number[]) { this.properties.view = value; }
/**
 * <p>Fill elements with a gradient instead of a solid color</p>
 * @param {?} value
 * @return {?}
 */
set gradient(value: boolean) { this.properties.gradient = value; }
/**
 * <p>Show or hide the legend</p>
 * @param {?} value
 * @return {?}
 */
set showLegend(value: boolean) { this.properties.showLegend = value; }
/**
 * <p>Legend position. Can be: top, right, bottom, left. Default: right.</p>
 * @param {?} value
 * @return {?}
 */
set legendPosition(value: string) { this.properties.legendPosition = value; }
/**
 * <p>Chart alignment. Can be: start, center, end. Default: start.</p>
 * @param {?} value
 * @return {?}
 */
set chartAlignment(value: string) { this.properties.chartAlignment = value; }
/**
 * <p>Show or hide the x axis</p>
 * @param {?} value
 * @return {?}
 */
set showXAxis(value: boolean) { this.properties.showXAxis = value; }
/**
 * <p>Show or hide the y axis</p>
 * @param {?} value
 * @return {?}
 */
set showYAxis(value: boolean) { this.properties.showYAxis = value; }
/**
 * <p>Show or hide the x axis label</p>
 * @param {?} value
 * @return {?}
 */
set showXAxisLabel(value: boolean) { this.properties.showXAxisLabel = value; }
/**
 * <p>Show or hide the y axis label</p>
 * @param {?} value
 * @return {?}
 */
set showYAxisLabel(value: boolean) { this.properties.showYAxisLabel = value; }
/**
 * <p>The x axis label text</p>
 * @param {?} value
 * @return {?}
 */
set xAxisLabel(value: string) { this.properties.xAxisLabel = value; }
/**
 * <p>The y axis label text</p>
 * @param {?} value
 * @return {?}
 */
set yAxisLabel(value: string) { this.properties.yAxisLabel = value; }
/**
 * <p>The color scheme name from the charts color scheme sets. Default color scheme is: forest</p>
 * <ul><li>vivid</li><li>natural</li><li>cool</li><li>fire</li>
 * <li>solar</li><li>air</li><li>aqua</li><li>flame</li><li>ocean</li>
 * <li>forest</li><li>horizon</li><li>neons</li><li>picnic</li><li>night</li>
 * <li>nightLights</li></ul>
 * @param {?} value
 * @return {?}
 */
set colorSchemeName(value: string) {
    if (!!value) {
      this.properties.colorSchemeName = value;
      this.properties.colorScheme = this.colorSchemes.find(s => s.name === value);
    }
  }
/**
 * <p>The color scheme of the chart - it is array with string css color values.
 * Can be set by providing array with colors or by selecting from existing color schemes palettes.
 * Default template color scheme is this:</p>
 * <pre>{{'{'}}
 *      domain: []
 * {{'}'}}</pre>
 * @param {?} value
 * @return {?}
 */
set colorScheme(value: ChartsColorScheme) { this.properties.colorScheme = value; }
/**
 * <p>The event will be emitted when the chart is clicked</p>
 */
onClick: EventEmitter<any> = new EventEmitter();
/**
 * @hidden 
 * <p>List of all charts color schemes</p>
 */
colorSchemes: any[];
/**
 * @hidden
 */
currentHeight: string;
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} eventService
 */
constructor(public elementRef: ElementRef,
              /**@hidden */ templateService: TemplateService,
public eventService: CfEventService) {
    super(elementRef, templateService);
    this.colorSchemes = ColorSchemes;
    this.styling = new ChartStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit(): void { }
/**
 * @hidden 
 * <p>The method will emit event when the chart is clicked</p>
 * @param {?} event
 * @return {?}
 */
onSelect(event) {
    this.onClick.emit(event);
    this.eventService.emitEvent(new EventDataModel({
      sourceTag: this.id,
      data: event,
      source: 'chart'
    }));
  }
/**
 * @hidden 
 * Method to update chart height when chart legend is on top/bottom of chart itself,
 * because in that case own ngx-chart element class="ngx-charts-outer" has column orientation
 * and element ngx-charts-chart can't recalculate it own height.
 * @param {?} number
 * @return {?}
 */
updateChartHeight(number) {
    if (
      (this.properties.legendPosition === 'top' || this.properties.legendPosition === 'bottom')
      && !!number && number !== this.currentHeight
    ) {
      this.currentHeight = number;
      this.cfChartHost.nativeElement.style.height = number + 'px';
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-chart',
  template: "<div class=\"cf-chart-container\"> <div [ngTemplateOutlet]=\"cfComponentChartTemplate\"></div> </div> ",
  styles: [":host { display: flex; } :host.left /deep/ .ngx-charts-outer { flex-direction: row-reverse; } :host.right /deep/ .ngx-charts-outer { flex-direction: row; } :host.left.start /deep/ .ngx-charts-outer, :host.right.start /deep/ .ngx-charts-outer { align-items: flex-start; } :host.left.center /deep/ .ngx-charts-outer, :host.right.center /deep/ .ngx-charts-outer { align-items: center; } :host.left.end /deep/ .ngx-charts-outer, :host.right.end /deep/ .ngx-charts-outer { align-items: flex-end; } :host.top /deep/ .ngx-charts-outer { flex-direction: column-reverse; } :host.bottom /deep/ .ngx-charts-outer { flex-direction: column; } :host.bottom.start /deep/ .ngx-charts-outer, :host.top.start /deep/ .ngx-charts-outer { align-items: flex-start; } :host.bottom.center /deep/ .ngx-charts-outer, :host.top.center /deep/ .ngx-charts-outer { align-items: center; } :host.bottom.end /deep/ .ngx-charts-outer, :host.top.end /deep/ .ngx-charts-outer { align-items: flex-end; } :host.top /deep/ .chart-legend, :host.bottom /deep/ .chart-legend { width: 100% !important; } :host.top /deep/ .chart-legend > div, :host.bottom /deep/ .chart-legend > div { width: 100% !important; } :host.top /deep/ .chart-legend .legend-wrap, :host.bottom /deep/ .chart-legend .legend-wrap { width: 100% !important; } :host /deep/ .cf-chart-zone, :host /deep/ .cf-chart-container { display: flex; width: 100%; } :host /deep/ .ngx-charts-outer { display: flex; } :host /deep/ * { outline: none !important; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: CfEventService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'cfComponentChartTemplate': [{ type: ContentChild, args: ['cfComponentChartTemplate', ] },],
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'data': [{ type: Input, args: ['data', ] },],
'view': [{ type: Input, args: ['view', ] },],
'gradient': [{ type: Input, args: ['gradient', ] },],
'showLegend': [{ type: Input, args: ['showLegend', ] },],
'legendPosition': [{ type: Input, args: ['legendPosition', ] },],
'chartAlignment': [{ type: Input, args: ['chartAlignment', ] },],
'showXAxis': [{ type: Input, args: ['showXAxis', ] },],
'showYAxis': [{ type: Input, args: ['showYAxis', ] },],
'showXAxisLabel': [{ type: Input, args: ['showXAxisLabel', ] },],
'showYAxisLabel': [{ type: Input, args: ['showYAxisLabel', ] },],
'xAxisLabel': [{ type: Input, args: ['xAxisLabel', ] },],
'yAxisLabel': [{ type: Input, args: ['yAxisLabel', ] },],
'colorSchemeName': [{ type: Input, args: ['colorSchemeName', ] },],
'colorScheme': [{ type: Input, args: ['colorScheme', ] },],
'onClick': [{ type: Output },],
};
}

function CfChartComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfChartComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfChartComponent.ctorParameters;
/** @type {?} */
CfChartComponent.propDecorators;
/**
 * @hidden
 * @type {?}
 */
CfChartComponent.prototype.cfComponentChartTemplate;
/**
 * @hidden
 * @type {?}
 */
CfChartComponent.prototype.cfChartHost;
/**
 * @hidden
 * @type {?}
 */
CfChartComponent.prototype.cfNgxChart;
/**
 * <p>Chart component model object. Refer to ../models/chart/chart.model.ts.</p>
 * @type {?}
 */
CfChartComponent.prototype.properties;
/**
 * <p>The styling object of the chart component. Refer to ../models/chart/chart-styling.model.ts</p>
 * @type {?}
 */
CfChartComponent.prototype.styling;
/**
 * <p>The event will be emitted when the chart is clicked</p>
 * @type {?}
 */
CfChartComponent.prototype.onClick;
/**
 * @hidden 
 * <p>List of all charts color schemes</p>
 * @type {?}
 */
CfChartComponent.prototype.colorSchemes;
/**
 * @hidden
 * @type {?}
 */
CfChartComponent.prototype.currentHeight;
/** @type {?} */
CfChartComponent.prototype.elementRef;
/**
 * @hidden
 * @type {?}
 */
CfChartComponent.prototype.eventService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
